import { useInvalidateQueries } from "@birdwatch/client";
import type { RealtimeEventData, RealtimeEventName } from "@birdwatch/common";
import { useCallback } from "react";

import { refreshKeysFor } from "./refresh-keys";

/**
 * Refresh what one change makes stale — for a mutation's `onSuccess`. Name the
 * same event the platform route announces for that write:
 *
 * ```ts
 * const refresh = useRefresh();
 * const update = useUpdateTasksByTaskId({
 *   onSuccess: (task) =>
 *     refresh("task.changed", { taskId: task.id, orderId: task.orderId, placeId: task.placeId }),
 * });
 * ```
 *
 * Returns a promise that settles when the refetches do, so `onSuccess` can return
 * it and keep the mutation pending until the screen shows the new data.
 */
export function useRefresh() {
  const invalidate = useInvalidateQueries();
  return useCallback(
    <E extends RealtimeEventName>(event: E, data: RealtimeEventData<E>) =>
      invalidate(...refreshKeysFor(event, data)),
    [invalidate],
  );
}
